/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 숫자 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/number/Number.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 21.
 */
import * as parent from '@components/form/field/input/Input';
import { FormatNumber } from '@utils/FormatNumber';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import './Number.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 숫자입력시 숫자포맷을 적용할지 여부
     */
    format?: boolean;

    /**
     * 숫자포맷을 적용할 지역코드
     */
    locale?: string;

    /**
     * 필드값
     */
    value?: number;

    /**
     * 증감값
     */
    step?: number;

    /**
     * 필드 최소값
     */
    minValue?: number;

    /**
     * 필드 최댓값
     */
    maxValue?: number;

    /**
     * 숫자조절 버튼을 보일지 여부
     */
    spinner?: boolean;

    /**
     * 수정 여부
     */
    editable?: boolean;

    /**
     * value 없을 경우 반환값(없으면 null)
     */
    defaultValue?: number;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Number extends parent.Input {
    /**
     * Input 확장타입
     */
    inputVariant = 'number';

    /**
     * 숫자입력시 숫자포맷을 적용할 지여부
     */
    format: boolean | null = null;

    /**
     * 숫자포맷을 적용할 지역코드
     */
    locale: string | null = null;

    /**
     * 증감값
     */
    step: number | null = null;

    /**
     * 필드 최소값
     */
    minValue: number | null = null;

    /**
     * 필드 최댓값
     */
    maxValue: number | null = null;

    /**
     * 숫자조절 버튼을 보일지 여부
     */
    spinner: boolean | null = null;

    /**
     * 숫자조절 버튼이 보일 때 텍스트 박스 수정 여부
     */
    editable: boolean | null = null;

    /**
     * 숫자포맷을 위한 객체
     */
    FormatNumber: FormatNumber;

    /**
     * 스피너 Dom
     */
    $spinner: Dom;

    /**
     * 값 변경 때 사용하는 타임아웃
     */
    spinTimeout: ReturnType<typeof setTimeout>;

    /**
     * 숫자 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Number> = null) {
        super(properties);

        this.format = this.properties.format === true;
        this.locale = this.properties.locale ?? Html.get('html').getAttr('lang') ?? 'ko';
        this.defaultValue = this.properties.defaultValue ?? null;
        this.FormatNumber = new FormatNumber(0, this.locale);
        this.inputAlign = this.properties.inputAlign ?? 'right';
        this.step = this.properties.step ?? 1;
        this.minValue = this.properties.minValue ?? null;
        this.maxValue = this.properties.maxValue ?? null;
        this.spinner = this.properties.spinner === true;
        this.editable = this.properties.editable !== false;
        this.keyboard = true;
    }

    /**
     * INPUT 필드 DOM 을 가져온다.
     *
     * @returns INPUT 필드 DOM
     */
    $getInput(): Dom<HTMLInputElement> {
        if (this.$input === null) {
            this.$input = super.$getInput(false, 'text');

            this.$input.on('input', async (me) => {
                let value = me.getValue();

                if (this.format == true && value !== null) {
                    const endsWithDot = value.endsWith(this.FormatNumber.decimalSeparator);
                    const startsWithMinus = value.startsWith('-');
                    this.FormatNumber.setValue(value.replace(/^-/, ''));
                    const oldCaret: number = me.getEl().selectionStart ?? 0;
                    const digitsBeforeCaret: number = this.FormatNumber.digits(value.slice(0, oldCaret));
                    let formatted = this.FormatNumber.format();

                    if (startsWithMinus == true) {
                        formatted = '-' + formatted;
                    }

                    if (endsWithDot == true) {
                        formatted += this.FormatNumber.decimalSeparator;
                    }

                    me.setValue(formatted);
                    let newCaret: number = this.FormatNumber.getOffsetFromDigitIndex(digitsBeforeCaret, formatted);
                    if (startsWithMinus == true && newCaret == 0) newCaret++;
                    if (endsWithDot == true && newCaret == formatted.length - 1) {
                        newCaret++;
                    }
                    me.getEl().setSelectionRange(newCaret, newCaret);
                }

                if (value.length > 0 && (await this.isNumber(value)) === true) {
                    await this.setValue(value, false, false);
                    const validation = this.getValidationHistory(value, true);
                    this.updateValidation(validation);
                } else {
                    await this.setValue(null, true, false);
                }
            });

            this.$input.on('blur', async (me) => {
                if (this.format == true) {
                    this.FormatNumber.setValue(me.getValue());
                    me.setValue(this.FormatNumber.format());
                }

                if (me.getValue().length == 0) {
                    if (this.defaultValue !== null) {
                        this.setValue(this.defaultValue);
                    }
                } else if ((await this.isNumber(me.getValue())) == false) {
                    const validation = this.getValidationHistory(me.getValue(), true);
                    this.updateValidation(validation);
                }
            });

            this.$input.on('keydown', (_, e) => {
                if (
                    e.key == 'Backspace' ||
                    e.key == 'Tab' ||
                    e.key == 'Delete' ||
                    e.key.search(/Arrow/) > -1 ||
                    e.metaKey == true ||
                    e.ctrlKey == true
                ) {
                    return;
                }

                if (e.key == 'Enter' || e.key == 'Escape') {
                    return;
                }

                if (e.key.search(/[0-9\-]/) == -1 && e.key != this.FormatNumber.decimalSeparator) {
                    e.preventDefault();
                }

                e.stopImmediatePropagation();
            });
        }

        return this.$input;
    }

    /**
     * 초기화버튼 DOM 을 가져온다.
     *
     * @returns 초기화버튼 DOM
     */
    $getClear(): Dom {
        if (this.$clear === null) {
            this.$clear = Html.create('i');

            this.$clear.on('click', (_, e) => {
                this.setValue(this.minValue ?? null);
                this.focus();
                e.preventDefault();
                e.stopImmediatePropagation();
            });
        }

        return this.$clear;
    }

    /**
     * 스피너를 가져온다.
     *
     * @return {Dom} $spinner
     */
    $getSpinner(): Dom {
        if (this.$spinner === undefined) {
            this.$spinner = Html.create('div', { 'data-role': 'spinner' });
            const $increase = Html.create('button', {
                type: 'button',
                'data-direction': 'increase',
                'tabindex': '-1',
            });
            $increase.html('<i></i>');
            this.$spinner.append($increase);
            $increase.on('mousedown', () => {
                this.startSpin(this.step);
            });
            $increase.on('mouseup', () => {
                this.stopSpin();
            });
            $increase.on('mouseout', () => {
                this.stopSpin();
            });

            const $decrease = Html.create('button', {
                type: 'button',
                'data-direction': 'decrease',
                'tabindex': '-1',
            });
            $decrease.html('<i></i>');
            $decrease.on('mousedown', () => {
                this.startSpin(this.step * -1);
            });
            $decrease.on('mouseup', () => {
                this.stopSpin();
            });
            $decrease.on('mouseout', () => {
                this.stopSpin();
            });
            this.$spinner.append($decrease);
        }

        return this.$spinner;
    }

    /**
     * 마우스가 활성화된 동안 지속해서 값을 변경한다.
     *
     * @param step - 변경할 단계
     */
    startSpin(step: number, is_interval: boolean = false): void {
        this.stopSpin();
        this.doStep(step);
        this.spinTimeout = setTimeout(this.startSpin.bind(this), is_interval == true ? 100 : 500, step, true);
    }

    /**
     * 값 변경을 중단한다.
     */
    stopSpin(): void {
        if (this.spinTimeout) {
            clearTimeout(this.spinTimeout);
            this.spinTimeout = null;
        }
    }

    /**
     * 값을 변경한다.
     *
     * @param step - 변경할 단계
     */
    doStep(step: number): void {
        const value = parseInt(this.$getInput().getValue(), 10);
        let change = value + step;
        if (this.minValue !== null) {
            change = Math.max(this.minValue, change);
        }

        if (this.maxValue !== null) {
            change = Math.min(this.maxValue, change);
        }

        this.setValue(change);
    }

    /**
     * 최소값을 설정한다.
     *
     * @param minValue - 최소값
     */
    setMinValue(minValue: number): void {
        this.minValue = minValue;

        if (this.minValue === null) {
            this.$getInput().removeAttr('min');
        } else {
            this.$getInput().setAttr('min', this.minValue.toString());
        }
    }

    /**
     * 최대값을 설정한다.
     *
     * @param maxValue - 최대값
     */
    setMaxValue(maxValue: number): void {
        this.maxValue = maxValue;

        if (this.maxValue === null) {
            this.$getInput().removeAttr('max');
        } else {
            this.$getInput().setAttr('max', this.maxValue.toString());
        }
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<number> {
        return this.value;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     */
    async setValue(value: number | string | null, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        let nValue: number = null;
        value = (value ?? '').toString();
        if (value.length == 0) {
            value = null;
        } else {
            nValue = value = this.FormatNumber.setValue(value).toNumber();
            if (isNaN(nValue) === true) {
                nValue = null;
            }
        }

        if (nValue !== null) {
            if (this.minValue !== null) {
                nValue = Math.max(nValue, this.minValue);
            }

            if (this.maxValue !== null) {
                nValue = Math.min(nValue, this.maxValue);
            }
        }

        if (is_update == true || (nValue !== null && value !== nValue)) {
            let displayValue: string;
            if (this.format === true && nValue !== null) {
                this.FormatNumber.setValue(nValue);
                displayValue = this.FormatNumber.format();
            } else {
                displayValue = (nValue ?? '').toString();
            }
            this.$getInput().setValue(displayValue);
        }

        await this.releaseValue(nValue, is_init);
    }

    /**
     * 필드값이 숫자인지만 간단하게 검증한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 숫자여부
     */
    private async isNumber(value: string = ''): Promise<boolean> {
        if (isNaN(this.FormatNumber.toNumber(value)) == true) {
            this.updateValidation({ valid: false, message: await Language.getError('NUMERIC_ONLY').getText() });
            return false;
        } else {
            this.updateValidation({ valid: true });
        }

        return true;
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 필드값 유효성결과
     */
    protected async validate(value: any = undefined): Promise<{ valid: boolean; message?: string }> {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        if (value === undefined) {
            value = await this.getValue();
        }

        let validation = this.getValidationHistory(value);
        if (validation === null) {
            validation = { valid: true };

            const isBlank = await this.isBlank(value);
            if (this.allowBlank === false && isBlank == true) {
                validation.valid = false;
                validation.message = await Language.getError('REQUIRED').getText();
            } else if (isNaN(this.FormatNumber.toNumber(value ?? this.defaultValue)) == true) {
                validation.valid = false;
                validation.message = await Language.getError('NUMERIC_ONLY').getText();
            } else if (typeof this.validator == 'function') {
                const results = await this.validator(value, this);
                if (typeof results == 'boolean') {
                    validation.valid = results;
                } else {
                    validation.valid = false;
                    validation.message = results;
                }
            }
        }

        return validation;
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.initValue === null && this.defaultValue !== null) {
            await this.setValue(this.defaultValue, true);
        }
        await super.renderState();
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        await super.renderField();

        if (this.spinner === true) {
            this.$getContent().append(this.$getSpinner());
        }

        if (this.minValue !== null) {
            this.setMinValue(this.minValue);
        }

        if (this.maxValue !== null) {
            this.setMaxValue(this.maxValue);
        }

        if (this.editable == false) {
            this.$getContent().setAttr('data-editable', 'false');
        }
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        const value = parseInt(this.$getInput().getValue() ?? '', 10);
        if (isNaN(value) === false) {
            if (e.key == 'ArrowUp' || e.key == 'ArrowDown') {
                await this.setValue(e.key == 'ArrowUp' ? value + 1 : value - 1, false, true);
                e.preventDefault();
                return false;
            }
        }

        await this.fireEvent('keyboard', [e, this]);
        return false;
    }
}
